import { Navigate } from 'react-router-dom';
import { useAuth } from '@/core/AuthContext';
import { useLanguage } from '@/core/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { BrandIcon } from '@/shared/components/BrandIcon';
import { Mail, LogOut, Clock, ShieldAlert, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';

const NoAccess = () => {
  const { user, profile, loading, signOut } = useAuth();
  const { t } = useLanguage();

  const handleSignOut = async () => {
    await signOut();
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <div className="flex items-center justify-center gap-3 mb-8">
          <BrandIcon className="h-9 w-9" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Factory Pilot</h1>
          </div>
        </div>

        <Card className="shadow-elevated">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
              <ShieldAlert className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <CardTitle className="text-xl">
              {t('auth.noAccess.title', 'Access Pending')}
            </CardTitle>
            <CardDescription className="text-base">
              {t('auth.noAccess.description', 'Your account is set up, but you don\'t have access to any factory yet.')}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* User info */}
            <div className="rounded-lg bg-muted/50 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{user?.email}</span>
              </div>
              {profile?.full_name && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{profile.full_name}</span>
                </div>
              )}
            </div>

            {/* Status explanation */}
            <Alert>
              <Clock className="h-4 w-4" />
              <AlertTitle>{t('auth.noAccess.whatHappensNext', 'What happens next?')}</AlertTitle>
              <AlertDescription className="mt-2 space-y-2">
                <p>{t('auth.noAccess.step1', 'An administrator needs to assign you to a tenant organization and grant factory access.')}</p>
                <p>{t('auth.noAccess.step2', 'Once approved, you\'ll be able to access your assigned factory modules.')}</p>
              </AlertDescription>
            </Alert>

            {/* Sign out button */}
            <Button 
              onClick={handleSignOut}
              variant="outline"
              className="w-full"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {t('auth.noAccess.signOut', 'Sign out')}
            </Button>

            {/* Help text */}
            <p className="text-xs text-center text-muted-foreground">
              {t('auth.noAccess.helpText', 'If you believe this is an error, please contact your system administrator or IT department.')}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NoAccess;
